import {
  Box,
  Button,
  CollectionPreferences,
  Modal,
  Pagination,
  SpaceBetween,
  Table,
} from '@amzn/awsui-components-core';
import { useCollection } from '@amzn/awsui-collection-hooks';
import { ContentEngagement } from '@amzn/awscat-pattern-library-service-model';
import React, { useEffect, useState } from 'react';
import pluralize from 'pluralize';
import { IMplServiceClient } from '../../api/IMplServiceClient';
import { MplServiceClientFactory } from '../../api/MplServiceClientFactory';
import constants from '../../common/constants';
import CsvDownloadButton from '../comment/CsvDownloadButton';
import {
  DEFAULT_PAGE_SIZE_OPTIONS,
  engagementCsvFormat,
  engagementColumnDefinitions,
} from './EngagementModalColumnDefinition';

interface ContentEngagementAdminModalProps {
  contentId: string;
  onCancel: () => void;
  visible: boolean;
}

const mplServiceClient: IMplServiceClient = MplServiceClientFactory.create();

const ContentEngagementAdminModal: React.FunctionComponent<ContentEngagementAdminModalProps> = (
  props: ContentEngagementAdminModalProps,
) => {
  const { contentId, onCancel, visible = true } = props;
  const [modalLoading, setModalLoading] = useState<boolean>(true);
  const [engagements, setEngagements] = useState<ContentEngagement[]>([]);
  const [engagementCount, setEngagementCount] = useState<number>(0);
  const [pageSize, setPageSize] = useState<number>(constants.TABLE_DEFAULT_PAGE_SIZE);

  useEffect(() => {
    const fetchEngagements = async () => {
      const engagementsResponse = await mplServiceClient.listContentEngagements(contentId);
      setEngagements(engagementsResponse.contentEngagements);
      setEngagementCount(engagementsResponse.engagementCount);
      setModalLoading(false);
    };
    if (visible) {
      fetchEngagements();
    }
  }, [contentId, visible]);

  const modalHeader =
    engagementCount > 0
      ? pluralize('unique engagement', engagementCount, true)
      : `There are no associated engagements`;

  const collectionPreferences = (
    <CollectionPreferences
      title={constants.PREFERENCES}
      confirmLabel={constants.CONFIRM_BUTTON_TEXT}
      cancelLabel={constants.CANCEL_BUTTON_TEXT}
      pageSizePreference={{
        title: constants.TABLE_PAGE_SELECTOR_MESSAGE,
        options: DEFAULT_PAGE_SIZE_OPTIONS,
      }}
      onConfirm={({ detail }) => setPageSize(detail.pageSize ?? constants.TABLE_DEFAULT_PAGE_SIZE)}
      preferences={{ pageSize }}
    />
  );
  const { items, collectionProps, paginationProps } = useCollection(engagements, {
    sorting: {
      defaultState: {
        // @ts-expect-error TS2532 - strict migration
        sortingColumn: { sortingField: engagementColumnDefinitions[0].id },
      },
    },
    pagination: {
      pageSize: constants.TABLE_DEFAULT_PAGE_SIZE,
    },
  });

  const engagementsTable = (
    <Table
      {...collectionProps}
      loading={modalLoading}
      columnDefinitions={engagementColumnDefinitions}
      resizableColumns
      wrapLines
      items={items}
      preferences={collectionPreferences}
      pagination={
        <Pagination
          {...paginationProps}
          ariaLabels={{
            nextPageLabel: 'Next page',
            previousPageLabel: 'Previous page',
            pageLabel: (pageNumber) => `Page ${pageNumber}`,
          }}
        />
      }
    />
  );

  const formActions = (
    <Box float="right">
      <SpaceBetween direction="horizontal" size="xs">
        <CsvDownloadButton
          data={engagements}
          format={engagementCsvFormat}
          filename={'engagements.csv'}
          buttonText={constants.DOWNLOAD_AS_CSV}
        />
        <Button onClick={onCancel} variant="normal">
          {constants.CLOSE_BUTTON_TEXT}
        </Button>
      </SpaceBetween>
    </Box>
  );

  return visible ? (
    <Modal
      onDismiss={onCancel}
      header={modalHeader}
      size="large"
      visible
      footer={formActions}
      closeAriaLabel="Close modal"
    >
      {engagementsTable}
    </Modal>
  ) : null;
};

export default ContentEngagementAdminModal;
